import { useMemo, useState, createElement } from "react";
import {
	Button,
	Dropdown,
	Image,
	Menu,
	Space,
	Tooltip,
	Typography,
	message,
	theme,
} from "antd";
import {
	DownloadOutlined,
	PlayCircleOutlined,
} from "@ant-design/icons";
import type { Portal, Section } from "../types";
import SectionRenderer from "./SectionRenderer";
import { sectionToHtml } from "../export/sectionToHtml";
import { exportToPdf, exportFullManualPdf } from "../export/exportToPdf";
import { getSectionImages } from "../autoImages";

const { Title, Paragraph } = Typography;

interface UserGuideProps {
	sections: Section[];
	portal: Portal;
	manualTitle: string;
	activeKey: string;
	onActiveKeyChange: (key: string) => void;
	onStartTour?: (section: Section) => void;
}

export default function UserGuide({
	sections,
	portal,
	manualTitle,
	activeKey,
	onActiveKeyChange,
	onStartTour,
}: UserGuideProps) {
	const { token } = theme.useToken();
	const [isExporting, setIsExporting] = useState(false);

	const activeSection = useMemo(
		() => sections.find((s) => s.key === activeKey) ?? sections[0],
		[sections, activeKey],
	);

	const autoImages = useMemo(
		() => (activeSection ? getSectionImages(portal, activeSection.key) : []),
		[portal, activeSection],
	);

	const menuItems = useMemo(
		() =>
			sections.map((section) => ({
				key: section.key,
				icon: createElement(section.icon),
				label: section.label,
			})),
		[sections],
	);

	async function handleDownloadCurrent() {
		if (!activeSection) return;
		try {
			setIsExporting(true);
			const html = await sectionToHtml(activeSection, portal);
			await exportToPdf(html, `${portal}-${activeSection.key}`);
		} catch (err) {
			console.error("Failed to export section", err);
			message.error("Could not export this section.");
		} finally {
			setIsExporting(false);
		}
	}

	async function handleDownloadManual() {
		try {
			setIsExporting(true);
			await exportFullManualPdf(
				sections,
				manualTitle,
				portal,
				`${portal}-user-guide`,
			);
		} catch (err) {
			console.error("Failed to export manual", err);
			message.error("Could not export the full manual.");
		} finally {
			setIsExporting(false);
		}
	}

	const downloadMenu = {
		items: [
			{ key: "section", label: "This section (.pdf)" },
			{ key: "manual", label: "Full manual (.pdf)" },
		],
		onClick: ({ key }: { key: string }) => {
			if (key === "section") void handleDownloadCurrent();
			else if (key === "manual") void handleDownloadManual();
		},
	};

	if (!activeSection) {
		return (
			<div style={{ padding: 24 }}>
				<Paragraph type="secondary">No sections to display.</Paragraph>
			</div>
		);
	}

	const canStartTour = Boolean(activeSection.tourKey && onStartTour);

	return (
		<div style={{ display: "flex", height: "100%", minHeight: 0 }}>
			<div
				style={{
					width: 260,
					flexShrink: 0,
					borderRight: `1px solid ${token.colorBorderSecondary}`,
					overflow: "auto",
					background: token.colorBgContainer,
				}}
			>
				<Menu
					mode="inline"
					selectedKeys={[activeSection.key]}
					onClick={({ key }) => onActiveKeyChange(key)}
					items={menuItems}
					style={{ border: "none" }}
				/>
			</div>

			<div
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
					minHeight: 0,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 12,
						padding: `${token.paddingSM}px ${token.paddingLG}px`,
						borderBottom: `1px solid ${token.colorBorderSecondary}`,
						background: token.colorBgContainer,
						flexShrink: 0,
					}}
				>
					<div style={{ minWidth: 0 }}>
						<Title level={4} style={{ margin: 0 }}>
							{activeSection.label}
						</Title>
						<Paragraph
							type="secondary"
							style={{ margin: 0, fontSize: token.fontSizeSM }}
							ellipsis={{ rows: 2, tooltip: activeSection.intro }}
						>
							{activeSection.intro}
						</Paragraph>
					</div>
					<Space>
						{canStartTour && (
							<Tooltip title="Start interactive tour on this page">
								<Button
									icon={<PlayCircleOutlined />}
									onClick={() => onStartTour?.(activeSection)}
								>
									Start tour
								</Button>
							</Tooltip>
						)}
						<Dropdown menu={downloadMenu} disabled={isExporting}>
							<Button
								type="primary"
								icon={<DownloadOutlined />}
								loading={isExporting}
							>
								Download
							</Button>
						</Dropdown>
					</Space>
				</div>

				<div
					style={{
						flex: 1,
						overflow: "auto",
						padding: `${token.paddingLG}px ${token.paddingXL}px`,
					}}
				>
					<SectionRenderer section={activeSection} />

					{autoImages.length > 0 && (
						<section style={{ marginTop: token.marginLG }}>
							<Title level={3} style={{ marginTop: 0 }}>
								Screenshots
							</Title>
							<Image.PreviewGroup>
								{autoImages.map((img) => (
									<figure
										key={img.name}
										style={{ margin: `0 0 ${token.marginLG}px` }}
									>
										<Image
											src={img.src}
											alt={img.name}
											style={{
												maxWidth: "100%",
												borderRadius: token.borderRadiusLG,
												border: `1px solid ${token.colorBorderSecondary}`,
											}}
										/>
										<figcaption
											style={{
												marginTop: 6,
												fontSize: token.fontSizeSM,
												color: token.colorTextSecondary,
												textAlign: "center",
											}}
										>
											{img.name}
										</figcaption>
									</figure>
								))}
							</Image.PreviewGroup>
						</section>
					)}
				</div>
			</div>
		</div>
	);
}
